import { useState } from "react";
import {
  Box,
  Button,
  Card,
  CardContent,
  CardHeader,
  Typography,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Checkbox,
  FormControlLabel,
  FormGroup,
  TextareaAutosize,
  Grid,
  Alert,
} from "@mui/material";
import { FileText, Send, Building } from "lucide-react";
import { toast } from "react-toastify";
import { KeyUri, config } from "../key";

export function PartnershipForm() {
  const [formData, setFormData] = useState({
    companyName: "",
    registrationNumber: "",
    businessType: "",
    contactPerson: "",
    designation: "",
    email: "",
    phone: "",
    alternatePhone: "",
    website: "",
    officeAddress: "",
    city: "",
    state: "",
    postalCode: "",
    operatingRegions: "",
    desiredTier: "",
    estimatedBookings: "",
    primaryServices: [],
    targetMarket: "",
    experienceYears: "",
    message: "",
    agreeTerms: false,
  });

const handleSubmit = async (e) => {
  e.preventDefault();

  if (!formData.agreeTerms) {
    toast.error("Please agree to the terms and conditions");
    return;
  }
  if (formData.primaryServices.length === 0) {
    toast.error("Please select at least one primary service");
    return;
  }

  try {
    const response = await fetch(KeyUri.BACKEND_URI + `/partnership-submit`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify(formData),
    });

    if (!response.ok) {
      throw new Error("Network response was not OK");
    }

    const result = await response.json();

    toast.success("Application submitted successfully! Our partnership team will contact you within 2-3 business days.");

    setFormData({
      companyName: "",
      registrationNumber: "",
      businessType: "",
      contactPerson: "",
      designation: "",
      email: "",
      phone: "",
      alternatePhone: "",
      website: "",
      officeAddress: "",
      city: "",
      state: "",
      postalCode: "",
      operatingRegions: "",
      desiredTier: "",
      estimatedBookings: "",
      primaryServices: [],
      targetMarket: "",
      experienceYears: "",
      message: "",
      agreeTerms: false,
    });
  } catch (error) {
    toast.error("Failed to submit application. Please try again later.");
    console.error("Submission error:", error);
  }
};


 const handleChange = (field, value) => {
  setFormData((prev) => ({ ...prev, [field]: value }));
};

 const handleServiceToggle = (service) => {
  setFormData((prev) => ({
    ...prev,
    primaryServices: prev.primaryServices.includes(service)
      ? prev.primaryServices.filter((s) => s !== service)
      : [...prev.primaryServices, service],
  }));
};
  const serviceOptions = [
    "Inbound Tourism",
    "Outbound Tourism",
    "Domestic Tours",
    "Group Tours",
    "Corporate Travel",
    "Adventure Tourism",
    "Pilgrimage Tourism",
    "MICE (Meetings, Incentives, Conferences, Exhibitions)",
  ];

  return (
    <Box sx={{ p:{xs:1 , lg: 4}, mt: {xs:2 , lg:0}}}>
      <Box mb={4}>
        <Typography variant="h4" gutterBottom>
          Partnership Application Form
        </Typography>
        <Typography color="text.secondary">
          Complete this comprehensive application to join our travel and tours
          logistics partnership network. Our team reviews all applications
          carefully and responds within 2-3 business days.
        </Typography>
      </Box>

      <Card>
        <CardHeader
          avatar={<FileText size={32} color="#1565c0" />}
          title={
            <Typography variant="h6" component="div">
              Partner Registration
            </Typography>
          }
          subheader="Please provide accurate and complete information. All mandatory fields are marked with *."
        />
        <CardContent>
          <form onSubmit={handleSubmit} noValidate>
            {/* Company Information */}
            <Box mb={4}>
              <Box display="flex" alignItems="center" mb={2} gap={1}>
                <Building size={20} color="#1565c0" />
                <Typography variant="subtitle1">
                  Company Information
                </Typography>
              </Box>
              <Grid container spacing={2}>
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    required
                    id="companyName"
                    label="Company/Agency Name *"
                    placeholder="Enter your company name"
                    value={formData.companyName}
                    onChange={(e) =>
                      handleChange("companyName", e.target.value)
                    }
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    required
                    id="registrationNumber"
                    label="Business Registration Number *"
                    placeholder="e.g., GST, Trade License Number"
                    value={formData.registrationNumber}
                    onChange={(e) =>
                      handleChange("registrationNumber", e.target.value)
                    }
                  />
                </Grid>

                <Grid item xs={12} md={6}>
                  <FormControl fullWidth required>
                    <InputLabel id="businessType-label">
                      Business Type *
                    </InputLabel>
                    <Select
                      labelId="businessType-label"
                      id="businessType"
                      value={formData.businessType}
                      label="Business Type *"
                      onChange={(e) =>
                        handleChange("businessType", e.target.value)
                      }
                    >
                      <MenuItem value="wellness_retreat">Wellness Retreat</MenuItem>
                      <MenuItem value="yoga_centre">Yoga centre</MenuItem>
                      <MenuItem value="wellness_tour_operator">Wellness Tour Operator</MenuItem>
                      <MenuItem value="travel_agency">Travel Agency</MenuItem>
                      <MenuItem value="tour_operator">Tour Operator</MenuItem>
                      <MenuItem value="dmc">
                        Destination Management Company (DMC)
                      </MenuItem>
                      <MenuItem value="hotel">Hotel/Resort</MenuItem>
                      <MenuItem value="transport">Transport Provider</MenuItem>
                      <MenuItem value="online_platform">
                        Online Travel Platform
                      </MenuItem>
                      <MenuItem value="other">Other</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>

                <Grid item xs={12} md={6}>
                  <FormControl fullWidth required>
                    <InputLabel id="experienceYears-label">
                      Years in Business *
                    </InputLabel>
                    <Select
                      labelId="experienceYears-label"
                      id="experienceYears"
                      value={formData.experienceYears}
                      label="Years in Business *"
                      onChange={(e) =>
                        handleChange("experienceYears", e.target.value)
                      }
                    >
                      <MenuItem value="0-1">Less than 1 year</MenuItem>
                      <MenuItem value="1-3">1-3 years</MenuItem>
                      <MenuItem value="3-5">3-5 years</MenuItem>
                      <MenuItem value="5-10">5-10 years</MenuItem>
                      <MenuItem value="10+">More than 10 years</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>

                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    id="website"
                    type="url"
                    label="Company Website"
                    placeholder="https://www.example.com"
                    value={formData.website}
                    onChange={(e) =>
                      handleChange("website", e.target.value)
                    }
                  />
                </Grid>
              </Grid>
            </Box>

            {/* Primary Contact Information */}
            <Box mb={4}>
              <Typography variant="subtitle1" mb={2}>
                Primary Contact Information
              </Typography>
              <Grid container spacing={2}>
                <Grid item xs={12} md={6}>
                  <TextField
                    required
                    fullWidth
                    id="contactPerson"
                    label="Contact Person Name *"
                    placeholder="Full name"
                    value={formData.contactPerson}
                    onChange={(e) =>
                      handleChange("contactPerson", e.target.value)
                    }
                  />
                </Grid>

                <Grid item xs={12} md={6}>
                  <TextField
                    required
                    fullWidth
                    id="designation"
                    label="Designation *"
                    placeholder="e.g., Owner, Director, Manager"
                    value={formData.designation}
                    onChange={(e) =>
                      handleChange("designation", e.target.value)
                    }
                  />
                </Grid>

                <Grid item xs={12} md={6}>
                  <TextField
                    required
                    fullWidth
                    id="email"
                    label="Email Address *"
                    type="email"
                    placeholder="email@company.com"
                    value={formData.email}
                    onChange={(e) => handleChange("email", e.target.value)}
                  />
                </Grid>

                <Grid item xs={12} md={6}>
                  <TextField
                    required
                    fullWidth
                    id="phone"
                    label="Primary Phone Number *"
                    type="tel"
                    placeholder="+91 9876543210"
                    value={formData.phone}
                    onChange={(e) => handleChange("phone", e.target.value)}
                  />
                </Grid>

                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    id="alternatePhone"
                    label="Alternate Phone Number"
                    type="tel"
                    placeholder="Optional backup contact"
                    value={formData.alternatePhone}
                    onChange={(e) =>
                      handleChange("alternatePhone", e.target.value)
                    }
                  />
                </Grid>
              </Grid>
            </Box>

            {/* Office Address */}
            <Box mb={4}>
              <Typography variant="subtitle1" mb={2}>
                Office Address
              </Typography>
              <TextField
                fullWidth
                required
                id="officeAddress"
                label="Street Address *"
                placeholder="Building name, street address"
                value={formData.officeAddress}
                onChange={(e) => handleChange("officeAddress", e.target.value)}
                sx={{ mb: 2 }}
              />
              <Grid container spacing={2}>
                <Grid item xs={12} md={4}>
                  <TextField
                    fullWidth
                    required
                    id="city"
                    label="City *"
                    placeholder="City"
                    value={formData.city}
                    onChange={(e) => handleChange("city", e.target.value)}
                  />
                </Grid>
                <Grid item xs={12} md={4}>
                  <TextField
                    fullWidth
                    required
                    id="state"
                    label="State/Province *"
                    placeholder="State"
                    value={formData.state}
                    onChange={(e) => handleChange("state", e.target.value)}
                  />
                </Grid>
                <Grid item xs={12} md={4}>
                  <TextField
                    fullWidth
                    required
                    id="postalCode"
                    label="Postal Code *"
                    placeholder="Postal code"
                    value={formData.postalCode}
                    onChange={(e) => handleChange("postalCode", e.target.value)}
                  />
                </Grid>
              </Grid>
            </Box>

            {/* Business Operations */}
            <Box mb={4}>
              <Typography variant="subtitle1" mb={2}>
                Business Operations & Partnership Details
              </Typography>
              <TextField
                fullWidth
                required
                id="operatingRegions"
                label="Primary Operating Regions *"
                placeholder="e.g., North India, South Asia, Europe"
                value={formData.operatingRegions}
                onChange={(e) => handleChange("operatingRegions", e.target.value)}
                sx={{ mb: 3 }}
              />

              <Typography variant="body1" mb={1}>
                Primary Services Offered * (Select all that apply)
              </Typography>
              <FormGroup>
                <Grid container spacing={1}>
                  {serviceOptions.map((service) => (
                    <Grid item xs={12} md={6} key={service}>
                      <FormControlLabel
                        control={
                          <Checkbox
                            checked={formData.primaryServices.includes(service)}
                            onChange={() => handleServiceToggle(service)}
                            name={service}
                          />
                        }
                        label={service}
                      />
                    </Grid>
                  ))}
                </Grid>
              </FormGroup>

              <Grid container spacing={2} mt={2}>
                <Grid item xs={12} md={6}>
                  <FormControl fullWidth required>
                    <InputLabel id="targetMarket-label">
                      Target Customer Segment *
                    </InputLabel>
                    <Select
                      labelId="targetMarket-label"
                      id="targetMarket"
                      value={formData.targetMarket}
                      label="Target Customer Segment *"
                      onChange={(e) => handleChange("targetMarket", e.target.value)}
                    >
                      <MenuItem value="budget">Budget Travelers</MenuItem>
                      <MenuItem value="midrange">Mid-range Travelers</MenuItem>
                      <MenuItem value="luxury">Luxury Travelers</MenuItem>
                      <MenuItem value="corporate">Corporate Clients</MenuItem>
                      <MenuItem value="groups">Group Tours</MenuItem>
                      <MenuItem value="mixed">Mixed Segments</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={12} md={6}>
                  <FormControl fullWidth required>
                    <InputLabel id="estimatedBookings-label">
                      Estimated Annual Bookings *
                    </InputLabel>
                    <Select
                      labelId="estimatedBookings-label"
                      id="estimatedBookings"
                      value={formData.estimatedBookings}
                      label="Estimated Annual Bookings *"
                      onChange={(e) =>
                        handleChange("estimatedBookings", e.target.value)
                      }
                    >
                      <MenuItem value="0-50">0-50 bookings</MenuItem>
                      <MenuItem value="50-150">50-150 bookings</MenuItem>
                      <MenuItem value="150-300">150-300 bookings</MenuItem>
                      <MenuItem value="300-500">300-500 bookings</MenuItem>
                      <MenuItem value="500+">500+ bookings</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>
              </Grid>

              <TextareaAutosize
                minRows={5}
                placeholder="Additional Information / Special Requirements"
                style={{ width: "100%", padding: 10, fontFamily: "inherit", fontSize: 16, marginTop: 8,  border: "1px solid rgba(0, 0, 0, 0.23)", // typical MUI textfield border color
    borderRadius: 4, 
    resize: "vertical"  }}
                value={formData.message}
                onChange={(e) => handleChange("message", e.target.value)}
              />
            </Box>

            {/* Terms and Conditions */}
            <Box
              display="flex"
              alignItems="start"
              bgcolor="blue.50"
              border="1px solid"
              borderColor="blue.200"
              borderRadius={1}
              p={2}
              mb={3}
            >
              <FormControlLabel
                control={
                  <Checkbox
                    checked={formData.agreeTerms}
                    onChange={(e) =>
                      handleChange("agreeTerms", e.target.checked)
                    }
                  />
                }
                label={
                  <Typography variant="body2" sx={{ cursor: "pointer" }}>
                    I agree to the partnership terms and conditions *
                  </Typography>
                }
              />
            </Box>

            {/* Submit Button */}
            <Box
              display="flex"
              flexDirection={{ xs: "column", sm: "row" }}
              justifyContent="space-between"
              alignItems="center"
              gap={2}
              pt={2}
              borderTop="1px solid"
              borderColor="divider"
            >
              <Typography variant="body2" color="text.secondary">
                Application processing time: 2-3 business days
              </Typography>
              <Button
                variant="contained"
                color="primary"
                type="submit"
                size="large"
                startIcon={<Send size={20} />}
              >
                Submit Partnership Application
              </Button>
            </Box>
          </form>
        </CardContent>
      </Card>

      <Grid container spacing={2} mt={4}>
        <Grid item xs={12} md={6}>
          <Box
            p={2}
            bgcolor="#ecf9f1"
            border="1px solid #c6efcd"
            borderRadius={1}
          >
            <Typography variant="subtitle2" color="success.main" mb={1}>
              Next Steps After Submission:
            </Typography>
            <ol style={{ paddingLeft: 20, margin: 0, color: "#2e7d32" }}>
              <li>Application review (1-2 business days)</li>
              <li>Verification call from our partnership team</li>
              <li>Partnership agreement signing</li>
              <li>Portal access & training session</li>
              <li>Account activation & voucher issuance</li>
            </ol>
          </Box>
        </Grid>

        <Grid item xs={12} md={6}>
          <Box
            p={2}
            bgcolor="#fff4e5"
            border="1px solid #ffe6b0"
            borderRadius={1}
          >
            <Typography variant="subtitle2" color="warning.main" mb={1}>
              Need Assistance?
            </Typography>
            <ul style={{ paddingLeft: 20, margin: 0, color: "#ff6f00" }}>
              <li>📧 Email: contact@thegreathimalayas.com</li>
              <li>📞 Phone: +91 98835 87965</li>
              <li>⏰ Office Hours: Mon-Sun, 9:00 AM - 6:00 PM</li>
             {/*} <li>🌐 Partner Portal: portal.travellogistics.com</li>*/}
            </ul>
          </Box>
        </Grid>
      </Grid>
    </Box>
  );
}
